// frontend-react/src/hooks/useMarketValues.js
import { useState, useEffect } from 'react';
import { leaguesAPI } from '../services/apiService';

/**
 * Fetch market values for players in a league. Keyed by player_id (MLB id).
 *
 * @param {string} leagueId - League UUID
 * @param {Array<number|string>|null} playerIds - scope the request to these player ids
 *        (the keeper roster's ~25 MLB ids). The all-league response is >1 MB and 500s —
 *        passing the roster ids makes it succeed (Rule 2: no catch-and-blank, pass the
 *        right param). Pass null only to fetch the whole league (legacy/unscoped callers).
 * @param {boolean} enabled - Whether to fetch (default true)
 * @returns {object} Market values state
 */
export const useMarketValues = (leagueId, playerIds = null, enabled = true) => {
  const [marketValues, setMarketValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Stable dep + comma-joined query param. Empty array = scoped-but-not-loaded-yet.
  const idsKey = Array.isArray(playerIds) ? playerIds.join(',') : (playerIds || '');

  useEffect(() => {
    if (!leagueId || !enabled) {
      setLoading(false);
      return;
    }
    // Scoped caller whose id set hasn't loaded yet — wait (do NOT fall back to all-league).
    if (Array.isArray(playerIds) && playerIds.length === 0) {
      setLoading(false);
      return;
    }

    const fetchMarketValues = async () => {
      try {
        setLoading(true);
        setError(null);

        // Phase 3 endpoint — scoped to the passed player_ids when provided.
        const data = await leaguesAPI.getMarketValues(leagueId, idsKey || null);

        if (data.success && data.market_values) {
          // Convert array to lookup object by player_id
          const valuesMap = {};
          data.market_values.forEach(mv => {
            valuesMap[mv.player_id] = {
              baseline_price: mv.pricing?.baseline_price,
              current_market_value: mv.pricing?.current_market_value,
              change_pct: mv.pricing?.change_pct,
              last_updated: mv.market_value_updated_at,
              tier: mv.pricing?.tier
            };
          });

          setMarketValues(valuesMap);
        }
      } catch (err) {
        console.error('Error fetching market values:', err);
        setError(err.message || 'Failed to load market values');
      } finally {
        setLoading(false);
      }
    };

    fetchMarketValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, enabled, idsKey]);

  return {
    marketValues,
    loading,
    error
  };
};
